import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { Toast, ToastContextType, ToastType } from '@/types/common';

const ToastContext = createContext<ToastContextType>({
  toasts: [],
  addToast: () => {},
  removeToast: () => {},
});

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback(({ type, message, title, duration = 5000 }: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substring(2, 9);
    const newToast: Toast = {
      id,
      type,
      message,
      title,
      duration,
    };

    setToasts((currentToasts) => [...currentToasts, newToast]);

    // Auto-remove toast after duration
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((currentToasts) => currentToasts.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
}

function ToastContainer() {
  const context = useContext(ToastContext);
  if (!context) return null;

  const { toasts, removeToast } = context;

  const getToastIcon = (type: ToastType): string => {
    switch (type) {
      case 'success':
        return 'fas fa-check-circle';
      case 'error':
        return 'fas fa-exclamation-circle';
      case 'warning':
        return 'fas fa-exclamation-triangle';
      case 'info':
        return 'fas fa-info-circle';
      default:
        return 'fas fa-bell';
    }
  };

  const getToastClasses = (type: ToastType): string => {
    const baseClasses = 'relative p-4 rounded-lg shadow-lg mb-4 flex items-start transition-all duration-300';
    switch (type) {
      case 'success':
        return `${baseClasses} bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-200`;
      case 'error':
        return `${baseClasses} bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200`;
      case 'warning':
        return `${baseClasses} bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200`;
      case 'info':
        return `${baseClasses} bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200`;
      default:
        return `${baseClasses} bg-gray-50 dark:bg-gray-900/30 text-gray-800 dark:text-gray-200`;
    }
  };

  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="fixed top-[4.5rem] right-4 z-50 w-full sm:max-w-[350px] md:max-w-[400px] space-y-4 pointer-events-none"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`${getToastClasses(toast.type)} animate-slide-left max-w-fit ml-auto`}
          role="alert"
        >
          <div className="flex-shrink-0 mr-3">
            <i className={getToastIcon(toast.type)} aria-hidden="true" />
          </div>
          <div className="flex-1 mr-2">
            {toast.title && (
              <h4 className="text-sm font-medium mb-1">{toast.title}</h4>
            )}
            <p className="text-sm">{toast.message}</p>
          </div>
          <button
            type="button"
            className="flex-shrink-0 ml-auto -mx-1.5 -my-1.5 p-1.5 opacity-50 hover:opacity-100 focus:opacity-100 pointer-events-auto"
            onClick={() => removeToast(toast.id)}
          >
            <span className="sr-only">Close</span>
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>
      ))}
    </div>
  );
}

// Custom hook to use toast context
export function useToast() {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

export default ToastContext;